import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Image } from '@/components/common/Image';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import TinyMCEEditor from '@/components/tiny-editor';
import { DISPLAY_MOMENT_DATE_TIME_HM, FILE_SIZE_IMAGE, FORUM, ROUTES, SUPPORTED_FORMATS, TOPIC } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { FORUM_TYPE, TOPIC_STATUS_TYPE, TOPIC_TYPE, TOPIC_TYPE_APPROVE } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { ChevronLeftIcon, CircleCheck, CircleX, RotateCcw, XIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useRevalidator } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import { formatDateTime } from '@/utils/datetime';
import ConfirmPopup from './ConfirmPopup';

// const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
// const FILE_SIZE = 5 * 1024 * 1024; // 5 MB

interface TopicFormProps {
    onFetch?: Function,
    isEdit?: boolean
}

export default observer(function TopicForm({ onFetch, isEdit = true }: TopicFormProps) {
    // hooks
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { code } = useParams();
    const navigate = useNavigate();

    // store
    const { topicStore: { topicDetail, createTopic, getTopicDetail, updateTopic, setObservable, updateApproveTopic },
        modalStore: { hideModal } } = useStore();
    // state
    const [imagePreview, setimagePreview] = useState<string | null>(null);
    const isCreatedByAdmin = topicDetail?.isCreatedByAdmin;
    const isTopicApproved = (topicDetail?.status === TOPIC_STATUS_TYPE.Published || topicDetail?.status === TOPIC_STATUS_TYPE.NotPublished || topicDetail?.isCreatedByAdmin);

    useEffect(() => {
        if (topicDetail) {
            form.reset({
                forum: topicDetail?.forum || FORUM_TYPE.FreeTopic,
                type: topicDetail?.type || TOPIC_TYPE.Notice,
                title: topicDetail?.title || '',
                imageAlt: topicDetail?.imageAlt || '',
                description: topicDetail?.description || '',
                image: topicDetail?.image || undefined,
                status: topicDetail?.status || TOPIC_STATUS_TYPE.NotPublished,
                note: topicDetail?.note || '',
                reason: topicDetail?.historyApproveTopic?.reason || ''
            });
            setimagePreview(typeof topicDetail?.image === 'string' ? topicDetail.image : null);
        }
    }, [topicDetail]);

    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    // validate Schema
    const validateSchema = yup.object().shape({
        forum: yup
            .number()
            .required('form_error_validate.required'),
        type: yup
            .number()
            .required('form_error_validate.required'),
        title: yup
            .string()
            .max(255, 'form_error_validate.title_maxlength')
            .required('form_error_validate.required'),
        description: yup
            .string()
            .test('not-empty', 'form_error_validate.required', (value) => {
                return value !== '<p><br></p>';
            })
            .required('form_error_validate.required'),
        image: yup
            .mixed()
            .nullable()
            .test('fileType', 'form_error_validate.topic_image_upload_format', (value) => {
                if (!value) return true;
                if (value instanceof File) {
                    return SUPPORTED_FORMATS.includes(value.type);
                }
                if (typeof value === 'string') {
                    return true;
                }
                return false;
            })
            .test('fileSize', 'form_error_validate.topic_image_upload_format', (value) => {
                if (!value) return true;
                if (typeof value === 'string') {
                    return true;
                }
                return value instanceof File && value.size <= FILE_SIZE_IMAGE;
            }),
        imageAlt: yup
            .string(),
        reason: yup
            .string()
            .max(1000, 'form_error_validate.max_length_1000'),
        status: yup
            .number()
            .required('form_error_validate.required'),
        note: yup
            .string()
            .max(5000, 'form_error_validate.max_length_5000')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            forum: FORUM_TYPE.FreeTopic,
            type: TOPIC_TYPE.Notice,
            title: '',
            description: '',
            image: undefined,
            reason: '',
            note: '',
            status: TOPIC_STATUS_TYPE.Published
        }
    });

    // functions
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const newimagePreview = URL.createObjectURL(file);
            setimagePreview(newimagePreview);
            form.setValue('image', file);
            form.trigger('image');
        }
    };

    const handleRemoveImage = () => {
        setimagePreview(null);
        form.setValue('image', '');
        form.trigger('image');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = async (data: FormData) => {
        const finalData = {
            ...data,
            ...(data.image ? { image: [data.image] } : { image: null }),
            ...(topicDetail?.historyApproveTopic?.id ?
                { historyApproveTopicId: topicDetail.historyApproveTopic.id } :
                {})
        };
        if (code) {
            console.log('finalData', finalData);
            const res = await flowResult(updateTopic(code, finalData));
            if (res) {
                navigate(ROUTES.forum.href);
                toastify('success', t('messages.update_success'));
            }
        } else {
            const { reason, ...payload } = finalData;
            const res = await flowResult(createTopic(payload));
            if (res) {
                toastify('success', t('messages.add_success'));
                // navigate(`${ROUTES.forum.href}/${res?.data?.code}`);
                navigate(`${ROUTES.forum.href}`);
            }
        }
    };
    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='flex flex-col gap-3 bg-white rounded  p-4 mt-4'>
                    {/* created by + created date */}
                    {isEdit && (
                        <div className='grid grid-cols-2 gap-20 gap-y-6'>
                            <FormField
                                control={form.control}
                                name='forum'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center gap-4 pb-4'>
                                            <Label className='label-1 w-1/5'>{t('words_title.created_by')}</Label>
                                            <div className='w-4/5 ml-3'>
                                                {
                                                    topicDetail?.creatorBy?.isDeleted ?
                                                        (
                                                            <div>{topicDetail?.creatorBy?.fullName ?? ''}</div>
                                                        ) :
                                                        (
                                                            <Link to={`/users/${topicDetail?.creatorBy?.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                                                {topicDetail?.creatorBy?.fullName ?? ''}
                                                            </Link>
                                                        )
                                                }
                                            </div>
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center gap-4 pb-4'>
                                            <Label className='label-1 w-1/5'>{t('words_title.created_date')}</Label>
                                            <div className='w-4/5'>
                                                {formatDateTime(topicDetail?.createdAt ?? '', DISPLAY_MOMENT_DATE_TIME_HM)}
                                            </div>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>
                    )}
                    {/* forum + type */}
                    <div className='grid grid-cols-2 gap-20 gap-y-6'>
                        <FormField
                            control={form.control}
                            name='forum'
                            // disabled={!isCreatedByAdmin && isEdit}
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex flex-col'>

                                        <div className='flex gap-4 py-2'>
                                            <Label className='label-1 w-1/5 mt-[14px]' required>
                                                {t('words_title.forum')}
                                            </Label>
                                            {/* RadioGroup container */}
                                            <FormControl className={`w-4/5 ml-3 rounded-lg ${(!isCreatedByAdmin && isEdit) ? 'bg-[rgba(240,240,240,1)] border-[rgba(215,215,215,1)] border text-black cursor-not-allowed' : 'border-[rgba(0,0,0,0.2)] border border-dashed'}`}>
                                                <RadioGroup
                                                    // disabled={!isCreatedByAdmin && isEdit}
                                                    value={field.value?.toString() || ''}
                                                    onValueChange={(value) => {
                                                        if (isCreatedByAdmin || !isEdit) field.onChange(value);
                                                    }}
                                                    className='flex flex-wrap gap-4  p-3'
                                                >
                                                    {Object.values(FORUM).map(({ key, value }) => (
                                                        <FormItem
                                                            key={key}
                                                            className='flex items-center gap-2 w-44'
                                                        >
                                                            <FormControl>
                                                                <RadioGroupItem
                                                                    id={`${key}Radio`}
                                                                    value={value.toString()}
                                                                // disabled={!isCreatedByAdmin && isEdit}
                                                                />
                                                            </FormControl>
                                                            <Label
                                                                htmlFor={`${key}Radio`}
                                                                className='!my-0'
                                                            >
                                                                {t(`words_title.${key}`)}
                                                            </Label>
                                                        </FormItem>
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        </div>
                                        {/* FormMessage */}
                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/5 ml-3' />
                                            <FormMessage className='w-4/5' />
                                        </div>
                                    </div>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='type'

                            // disabled={!isCreatedByAdmin && isEdit}
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex flex-col'>

                                        <div className='flex gap-4 py-2'>
                                            <Label className='label-1 w-1/5 mt-[14px]' required>
                                                {t('words_title.type_of_topic')}
                                            </Label>
                                            {/* RadioGroup container */}
                                            <FormControl className={`w-4/5 rounded-lg ${(!isCreatedByAdmin && isEdit) ? 'bg-[rgba(240,240,240,1)] border-[rgba(215,215,215,1)] border text-black cursor-not-allowed' : 'border-[rgba(0,0,0,0.2)] border border-dashed '}`}>
                                                <RadioGroup
                                                    // disabled={!isCreatedByAdmin && isEdit}

                                                    value={field.value?.toString() || ''}
                                                    onValueChange={(value) => {
                                                        if (isCreatedByAdmin || !isEdit) field.onChange(value);
                                                    }}
                                                    className='flex flex-wrap gap-4  p-3'
                                                >
                                                    {Object.values(TOPIC)
                                                        .filter(({ value }) => {
                                                            if (!isEdit || isCreatedByAdmin) {
                                                                return [TOPIC_TYPE.Advertisement.toString(), TOPIC_TYPE.Notice.toString()].includes(value.toString());
                                                            }

                                                            return true;
                                                        })
                                                        .map(({ key, value }) => (
                                                            <FormItem key={key} className='flex items-center gap-2 w-44'>
                                                                <FormControl>
                                                                    <RadioGroupItem

                                                                        id={`${key}Radio`}
                                                                        value={value.toString()}
                                                                    // disabled={!isCreatedByAdmin && isEdit}
                                                                    />
                                                                </FormControl>
                                                                <Label htmlFor={`${key}Radio`} className='!my-0'>
                                                                    {t(`words_title.${key}`)}
                                                                </Label>
                                                            </FormItem>
                                                        ))}

                                                </RadioGroup>
                                            </FormControl>
                                        </div>
                                        {/* FormMessage */}
                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/5' />
                                            <FormMessage className='w-4/5' />
                                        </div>
                                    </div>
                                </FormItem>
                            )}
                        />

                    </div>
                    {/* title */}
                    <div className=''>
                        <FormField
                            control={form.control}
                            name='title'
                            // disabled={!isCreatedByAdmin && isEdit}
                            render={({ field }) => (
                                <>
                                    <FormItem className='flex gap-4 items-center space-y-0'>
                                        <Label className='label-1 w-[10%]' required>{t('words_title.title')}</Label>
                                        <div className='w-[90%]'>
                                            <FormControl>
                                                <Input
                                                    readOnly={!isCreatedByAdmin && isEdit}
                                                    className={(!isCreatedByAdmin && isEdit) ? 'bg-[rgba(240,240,240,1)] border-[rgba(215,215,215,1)] text-black cursor-not-allowed disabled:!opacity-100' : ''}
                                                    type='text'
                                                    {...field}
                                                />
                                            </FormControl>
                                        </div>
                                    </FormItem>
                                    <div className='flex items-center gap-4 mt-1'>
                                        <div className='w-[10%]'></div>
                                        <FormMessage className='w-5/6' />
                                    </div>
                                </>
                            )}
                        />
                    </div>
                    {/* description */}
                    <div className='mt-4'>
                        <FormField
                            // disabled={!isCreatedByAdmin && isEdit}
                            control={form.control}
                            name='description'
                            render={({ field }) => (
                                <>
                                    <FormItem className='flex gap-4 items-start space-y-0'>
                                        <Label className='label-1 w-[10%]' required>{t('words_title.description')}</Label>
                                        <div className='w-[90%]'>
                                            <FormControl className=''>
                                                <div className={classNames(code ? 'h-[32rem]' : 'h-96')}>
                                                    {(typeof isCreatedByAdmin !== 'undefined' || !isEdit) && (
                                                        <TinyMCEEditor
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            placeholder={t('placeholder.click_here_to_enter')}
                                                            height={code ? 500 : 380}
                                                            disabled={!isCreatedByAdmin && isEdit}
                                                        />
                                                    )}
                                                </div>
                                            </FormControl>
                                            <FormMessage className='mt-1' />
                                        </div>
                                    </FormItem>
                                </>
                            )}
                        />
                    </div>
                    {/* image topic + alt text */}
                    <div className='grid grid-cols-2 gap-20'>
                        <div>
                            <FormField
                                // disabled={!isCreatedByAdmin && isEdit}
                                control={form.control}
                                name='image'
                                render={({ field: { onChange, value, ref, ...rest } }) => (
                                    <>
                                        <FormItem className='flex gap-4 space-y-0 mt-2'>
                                            <div className='flex w-1/5 h-10 mt-[14px]'>
                                                <Label className='label-1'>{t('words_title.image_of_topic')}</Label>
                                            </div>
                                            <div className='w-4/5 flex ml-3'>
                                                <div
                                                    className={`flex justify-between w-full rounded-lg p-5  ${!isCreatedByAdmin && isEdit ?
                                                        'bg-[rgba(240,240,240,1)] border-[rgba(215,215,215,1)] border text-black cursor-not-allowed' :
                                                        'border-[rgba(0,0,0,0.2)] border border-dashed'}`}
                                                >
                                                    <div>

                                                        <FormControl>
                                                            <Input
                                                                readOnly={!isCreatedByAdmin && isEdit}
                                                                type='file'
                                                                accept='image/*'
                                                                ref={fileInputRef}
                                                                onChange={handleImageChange}
                                                                className='hidden'
                                                                {...rest}
                                                            />
                                                        </FormControl>
                                                        <Button type='button' disabled={!isCreatedByAdmin && isEdit} className='w-fit px-4' onClick={() => fileInputRef.current?.click()}>{t('button.choose_image')}</Button>
                                                        <p className='mt-2 text-[0.8125rem] text-text-4'>{t('sentences.topic_image_note')}</p>
                                                        <FormMessage className='mt-1' />
                                                    </div>
                                                    {imagePreview && (
                                                        <div className='flex justify-end'>
                                                            <div className='relative w-fit'>
                                                                <Image
                                                                    src={imagePreview}
                                                                    alt='Job-image'
                                                                    className='w-auto min-w-24 h-24 object-cover rounded-lg'
                                                                />
                                                                <button
                                                                    type='button'
                                                                    disabled={!isCreatedByAdmin && isEdit}
                                                                    title='remove'
                                                                    onClick={handleRemoveImage}
                                                                    className={`absolute top-1 left-1 bg-black bg-opacity-80 rounded-full p-1 shadow-lg ${(!isCreatedByAdmin && isEdit) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                >
                                                                    <XIcon className='w-3 h-3 text-white' />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </FormItem>
                                    </>
                                )}
                            />
                        </div>
                        <div>
                            <FormField
                                control={form.control}
                                name='imageAlt'
                                render={({ field }) => (
                                    <>
                                        <FormItem className='flex gap-4 items-center space-y-0 mt-2'>
                                            <Label className='label-1 w-1/5'>{t('words_title.alt_text')}</Label>
                                            <div className='w-4/5'>
                                                <FormControl>
                                                    <Input
                                                        type='text'
                                                        maxLength={255}
                                                        {...field}
                                                    />
                                                </FormControl>
                                            </div>
                                        </FormItem>
                                    </>
                                )}
                            />
                        </div>
                    </div>

                </div>
                <div className='flex flex-col gap-5 bg-white rounded  p-4 mt-4'>
                    {/* status + (rejected by || approved by) */}
                    <div className='grid grid-cols-2 gap-20 gap-y-6'>
                        <FormField
                            control={form.control}
                            name='status'
                            // disabled={!isTopicApproved && isEdit}
                            render={({ field }) => (
                                <FormItem className='disabled:!opacity-100'>
                                    <div className='flex items-center'>
                                        <Label className='w-1/5 label-1' required>{t('words_title.status')}</Label>
                                        <Select
                                            key={field.value}
                                            disabled={!isTopicApproved && isEdit}

                                            onValueChange={(value) => {
                                                // if (isCreatedByAdmin || !isEdit) field.onChange(Number(value));
                                                field.onChange(Number(value));
                                            }}
                                            value={field.value.toString()}
                                            className='w-4/5 ml-[26px]'
                                        >
                                            <FormControl>
                                                <SelectTrigger className={(!isTopicApproved && isEdit) ?
                                                    'bg-[rgba(240,240,240,1)] rounded-lg border-[rgba(215,215,215,1)] text-black cursor-not-allowed disabled:!opacity-100' :
                                                    ''}
                                                >
                                                    <SelectValue placeholder={t('placeholder.choose')} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>

                                                <SelectItem
                                                    className='hover:cursor-pointer'
                                                    value={TOPIC_STATUS_TYPE.Published.toString()}
                                                >
                                                    <span>{t('words_title.published')}</span>
                                                </SelectItem>
                                                <SelectItem
                                                    className='hover:cursor-pointer'
                                                    value={TOPIC_STATUS_TYPE.NotPublished.toString()}
                                                >
                                                    <span>{t('words_title.not_published')}</span>
                                                </SelectItem>
                                                {(!isTopicApproved && isEdit) && (
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={TOPIC_STATUS_TYPE.WaitingToApprove.toString()}
                                                    >
                                                        <span>{t('words_title.waiting_to_approve')}</span>
                                                    </SelectItem>
                                                )}
                                                {(!isTopicApproved && isEdit) && (
                                                    <SelectItem
                                                        className='hover:cursor-pointer disabled:!opacity-100'
                                                        value={TOPIC_STATUS_TYPE.Rejected.toString()}
                                                    >
                                                        <span>{t('words_title.rejected')}</span>
                                                    </SelectItem>
                                                )}

                                            </SelectContent>
                                        </Select>
                                    </div>

                                </FormItem>
                            )}
                        />
                        {(isEdit && !isCreatedByAdmin && topicDetail?.status !== undefined &&
                            [TOPIC_STATUS_TYPE.Published, TOPIC_STATUS_TYPE.NotPublished, TOPIC_STATUS_TYPE.Rejected].includes(topicDetail.status)) &&
                            (
                                <FormField
                                    control={form.control}
                                    name='forum'
                                    render={({ field }) => (
                                        <FormItem className='h-full'>
                                            <div className='flex items-center gap-4 h-full'>
                                                <Label className='label-1 w-1/5'>
                                                    {(topicDetail?.status === TOPIC_STATUS_TYPE.Published || topicDetail?.status === TOPIC_STATUS_TYPE.NotPublished) ?
                                                        t('words_title.approved_by') :
                                                        topicDetail?.status === TOPIC_STATUS_TYPE.Rejected ?
                                                            t('words_title.rejected_by') :
                                                            ''}

                                                </Label>
                                                <div className='w-4/5'>
                                                    {
                                                        (topicDetail?.historyApproveTopic?.approveBy?.isDeleted || !topicDetail?.historyApproveTopic?.approveBy?.fullName) ?
                                                            (
                                                                <div>{topicDetail?.historyApproveTopic?.approveBy?.fullName ?? ''}</div>
                                                            ) :
                                                            (
                                                                <Link to={`/users/${topicDetail?.historyApproveTopic?.approveBy?.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                                                    {topicDetail?.historyApproveTopic?.approveBy?.fullName ?? ''}
                                                                </Link>
                                                            )
                                                    }
                                                </div>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            )}
                    </div>
                    {/* rejected date + (reason || approved date) */}
                    {(isEdit && !isCreatedByAdmin) && (
                        <div className='grid grid-cols-2 gap-20 gap-y-6'>
                            <FormField
                                control={form.control}
                                name='forum'
                                render={({ field }) => (
                                    <FormItem className='h-full'>
                                        <div className='flex items-start gap-4 h-full'>
                                            <Label className='label-1 w-1/5'>
                                                {topicDetail?.status === TOPIC_STATUS_TYPE.Rejected ? t('words_title.rejected_date') : ''}
                                            </Label>
                                            <div className='w-4/5 ml-3'>
                                                {formatDateTime(topicDetail?.status === TOPIC_STATUS_TYPE.Rejected ? topicDetail?.historyApproveTopic?.updatedAt : '', DISPLAY_MOMENT_DATE_TIME_HM)}
                                            </div>
                                        </div>
                                    </FormItem>
                                )}
                            />
                            {topicDetail?.status === TOPIC_STATUS_TYPE.Rejected ?
                                (
                                    <FormField
                                        control={form.control}
                                        name='reason'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-start gap-4'>
                                                    <Label className='label-1 w-1/5'>{t('words_title.reason')}</Label>
                                                    <FormControl className='w-4/5'>
                                                        <Textarea
                                                            className='resize-none h-[133px]'
                                                            value={field.value ?? ''}
                                                            maxLength={1000}
                                                            onChange={e => field.onChange(e.target.value)}
                                                        />
                                                    </FormControl>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                ) :
                                (topicDetail?.status !== TOPIC_STATUS_TYPE.WaitingToApprove && (
                                    <FormField
                                        control={form.control}
                                        name='forum'
                                        render={() => (
                                            <FormItem>
                                                <div className='flex  gap-4 pb-4'>
                                                    <Label className='label-1 w-1/5'>
                                                        {(topicDetail?.status === TOPIC_STATUS_TYPE.Published ||
                                                            topicDetail?.status === TOPIC_STATUS_TYPE.NotPublished) &&
                                                            t('words_title.approved_at')}
                                                    </Label>
                                                    <div className='w-4/5'>
                                                        {(topicDetail?.status === TOPIC_STATUS_TYPE.Published || topicDetail?.status === TOPIC_STATUS_TYPE.NotPublished) &&
                                                            formatDateTime(topicDetail?.historyApproveTopic?.updatedAt ?? '', DISPLAY_MOMENT_DATE_TIME_HM)}
                                                    </div>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                ))}

                        </div>
                    )}
                    {/* Note */}
                    {true && (
                        <div>
                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-start gap-4'>
                                            <Label className='label-1 w-[10%]'>{t('words_title.note')}</Label>
                                            <FormControl className='w-[90%]'>
                                                <Textarea
                                                    className='resize-none h-[133px]'
                                                    value={field.value ?? ''}
                                                    maxLength={5000}
                                                    onChange={e => field.onChange(e.target.value)}
                                                />
                                            </FormControl>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>
                    )}

                </div>
                <div className='flex justify-between bg-white rounded p-4 mx-auto gap-10 mt-4'>
                    {isEdit ?
                        (
                            (topicDetail?.status &&
                                topicDetail?.status !== TOPIC_STATUS_TYPE.Rejected &&
                                topicDetail?.status !== TOPIC_STATUS_TYPE.WaitingToApprove) ?
                                (
                                    <div></div>
                                ) :
                                (
                                    <div>
                                        {topicDetail?.status === TOPIC_STATUS_TYPE.WaitingToApprove ?
                                            (
                                                <div className='flex flex-row'>
                                                    <ConfirmPopup

                                                        type={TOPIC_TYPE_APPROVE.REJECT}
                                                    />

                                                    <ConfirmPopup

                                                        type={TOPIC_TYPE_APPROVE.APPROVE}
                                                    />

                                                </div>
                                            ) :
                                            (
                                                <div>
                                                    <ConfirmPopup

                                                        type={TOPIC_TYPE_APPROVE.RE_PUBLIC}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                )
                        ) :
                        (<div></div>)}

                    <div className={` flex flex-row gap-6  ${isEdit ? 'w-1/3 justify-end' : 'w-1/3 justify-end'}`}>
                        <Button
                            type='button'
                            className='w-1/3'
                            variant='outline'
                            onClick={() => {
                                navigate(ROUTES.forum.href);
                            }}
                        >
                            {t('button.cancel')}
                        </Button>
                        <Button type='submit' disabled={form.formState.isSubmitting} className='w-1/3'>{t('button.save')}</Button>
                    </div>
                </div>
                <div className='h-7'>

                </div>
            </form>
        </Form>
    );
});
